import { arc, type DefaultArcObject, type PieArcDatum } from 'd3-shape';

import { SUBPIXEL_SLICE_MARKER_THICKNESS } from '../constants.js';
import { InternalSliceData } from '../types/internal/pie-chart.js';

/**
 * Builds a subpixel-width (fixed-pixel-thickness) wedge marking where a single aggregated
 * subpixel slice would sit if it were wide enough to render. The marker is
 * positioned proportionally within the `subpixel-slices` bucket arc and filled
 * with the hovered slice's own color.
 *
 * @returns the marker path and color, or `undefined` when the hovered id is not
 * one of the bucket's grouped slices (or the geometry is degenerate).
 */
export const buildSubpixelSliceMarker = (
  bucketArc: PieArcDatum<InternalSliceData>,
  hoveredId: string,
  innerRadius: number,
  outerRadius: number,
): { path: string; color: string } | undefined => {
  const groupedSlices = bucketArc.data.groupedSlices;
  if (!groupedSlices || outerRadius <= 0) {
    return undefined;
  }

  let valueBefore = 0;
  let totalValue = 0;
  let target: InternalSliceData | undefined;
  for (const slice of groupedSlices) {
    if (slice.id === hoveredId) {
      target = slice;
      valueBefore = totalValue;
    }
    totalValue += slice.value;
  }

  if (!target || totalValue <= 0) {
    return undefined;
  }

  const span = bucketArc.endAngle - bucketArc.startAngle;
  const center =
    bucketArc.startAngle +
    ((valueBefore + target.value / 2) / totalValue) * span;
  const halfThickness = SUBPIXEL_SLICE_MARKER_THICKNESS / 2 / outerRadius;

  const path = arc()({
    innerRadius,
    outerRadius,
    startAngle: center - halfThickness,
    endAngle: center + halfThickness,
  } as DefaultArcObject);

  return path ? { path, color: target.color.Default } : undefined;
};
